import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const ProjectNavigation = ({ currentPage, projectId, project, onTabChange, hasSharepointConnection, activeTab, hasAtas }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const isDocumentAvailable = (documentType) => {
    switch (documentType) {
      case 'produktval':
        return project?.shoppingList?.status !== 'missing';
      case 'rumsbeskrivning':
        return project?.rumsa?.status !== 'missing';
      case 'tidplan':
        return project?.timeline?.status !== 'missing';
      case 'ritningar':
        return !!project?.architect;
      case 'underlag':
        return hasSharepointConnection !== false;
      case 'avtal':
        return true;
      case 'behorigheter':
        return !!project?.builder;
      case 'kundinfo':
        // Alltid tillgänglig — till skillnad från Produktval och Rumsbeskrivning
        // finns det inget dokument som först måste laddas upp av oss
        return true;
      case 'dokumentation':
        return project?.dokumentationChecklist?.length > 0;
      case 'ata':
        return project?.status === 'Pågående' || hasAtas;
      case 'overview':
        return true;
      case 'erbjudanden':
        // Rabatterna gäller oavsett var projektet befinner sig
        return true;
      default:
        return false;
    }
  };

  const activePage = activeTab || currentPage;

  const tabs = [
    { key: 'overview', label: 'Översikt', action: () => navigate('/myprojects'), available: true },
    { key: 'produktval', label: 'Produktval', available: isDocumentAvailable('produktval') },
    { key: 'rumsbeskrivning', label: 'Rumsbeskrivning', available: isDocumentAvailable('rumsbeskrivning') },
    { key: 'tidplan', label: 'Tidplan', path: 'timeline', available: isDocumentAvailable('tidplan') },
    { key: 'kundinfo', label: 'Kundinfo', available: isDocumentAvailable('kundinfo') },
    { key: 'ritningar', label: 'Ritningar', available: isDocumentAvailable('ritningar') },
    { key: 'underlag', label: isDocumentAvailable('underlag') ? 'Underlag' : 'Underlag (Offline)', available: isDocumentAvailable('underlag') },
    { key: 'avtal', label: 'Avtal', available: isDocumentAvailable('avtal') },
    { key: 'ata', label: 'ÄTA', available: isDocumentAvailable('ata') },
    { key: 'behorigheter', label: 'Behörigheter', available: isDocumentAvailable('behorigheter') },
    { key: 'dokumentation', label: 'Dokumentation', available: isDocumentAvailable('dokumentation') },
    { key: 'erbjudanden', label: 'Erbjudanden', available: isDocumentAvailable('erbjudanden'), highlight: true },
  ];

  const handleTabClick = (tab) => {
    if (!tab.available) return;
    if (tab.action) {
      tab.action();
    } else {
      const path = `/myproject/${projectId}/${tab.path || tab.key}`;
      onTabChange ? onTabChange(path) : navigate(path);
    }
    setMenuOpen(false);
  };

  const activeLabel = tabs.find(t => t.key === activePage)?.label || 'Översikt';

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  return (
    <div className="bg-white border-b">
      {/* Desktop: horizontal tabs */}
      <nav className="hidden sm:flex px-6 space-x-4">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabClick(tab)}
            className={`py-3 px-3 border-b-2 whitespace-nowrap text-sm ${
              activePage === tab.key
                ? 'text-leadme-pink border-leadme-pink'
                : tab.highlight
                  ? 'text-leadme-pink font-medium bg-pink-50 rounded-t-lg border-transparent hover:bg-pink-100 hover:text-leadme-pink-dark hover:border-leadme-pink-dark'
                  : tab.available
                    ? 'text-gray-500 border-transparent hover:text-leadme-pink-dark hover:border-leadme-pink-dark'
                    : 'text-gray-300 cursor-not-allowed border-transparent'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Mobile: hamburger dropdown */}
      <div className="sm:hidden relative" ref={menuRef}>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="w-full px-4 py-3 flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span className="text-sm font-medium text-leadme-pink">{activeLabel}</span>
          </div>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {menuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-50 max-h-[60vh] overflow-y-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabClick(tab)}
                className={`w-full px-4 py-3 text-left text-sm border-b border-gray-100 last:border-b-0 ${
                  activePage === tab.key
                    ? 'text-leadme-pink bg-pink-50 font-medium'
                    : tab.highlight
                      ? 'text-leadme-pink bg-pink-50/60 font-medium hover:bg-pink-50'
                      : tab.available
                        ? 'text-gray-700 hover:bg-gray-50'
                        : 'text-gray-300 cursor-not-allowed'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectNavigation;
